import { Component, signal, computed } from "@angular/core";
import { RouterLink } from "@angular/router";
import { Icon } from "./icon";
import { Reveal } from "./reveal";
import { Tilt } from "./tilt";

type CategoryKey = "heritage" | "wildlife" | "hills" | "coast" | "city";

interface Category {
  key: CategoryKey;
  label: string;
  icon: string;
}

interface Attraction {
  name: string;
  category: CategoryKey;
  blurb: string;
  image?: string;
  tag?: string;
  signature?: boolean;
  span?: string;
}

const CATEGORIES: Category[] = [
  { key: "heritage", label: "Culture & Heritage", icon: "temple" },
  { key: "wildlife", label: "Wildlife & Safari", icon: "compass" },
  { key: "hills", label: "Hill Country & Nature", icon: "mountain" },
  { key: "coast", label: "Beaches & Adventure", icon: "waves" },
  { key: "city", label: "City Life", icon: "camera" },
];

const ATTRACTIONS: Attraction[] = [
  // Culture & Heritage
  {
    name: "Sigiriya",
    category: "heritage",
    signature: true,
    span: "col-span-2 row-span-2",
    image: "./tour-sigiriya-1.png",
    tag: "UNESCO World Heritage",
    blurb:
      "A 5th-century rock fortress rising 200 metres from the jungle floor - frescoes, a mirror wall, and the ruins of King Kashyapa's sky palace at the summit.",
  },
  {
    name: "Temple of the Tooth Relic",
    category: "heritage",
    signature: true,
    image: "./attraction-temple-of-tooth-1.jpg",
    tag: "Kandy",
    blurb:
      "Kandy's Sri Dalada Maligawa houses Buddhism's most venerated relic - a tooth of the Buddha, brought to Sri Lanka in the 4th century AD.",
  },
  {
    name: "Polonnaruwa Ancient City",
    category: "heritage",
    signature: true,
    image: "./tour-polonnaruwa-1.jpg",
    tag: "UNESCO · \"Monkey Kingdom\"",
    blurb:
      "The 12th-century royal capital's Gal Vihara Buddhas, carved from a single granite face, plus toque macaque troops that gave the ruins their nickname.",
  },
  {
    name: "Gangaramaya Temple",
    category: "heritage",
    signature: true,
    image: "./attraction-gangaramaya-1.jpg",
    tag: "Colombo",
    blurb:
      "An eclectic Colombo temple mixing Sri Lankan, Thai, Indian and Chinese architecture around Beira Lake, with a museum wing that includes a vintage Rolls-Royce.",
  },
  {
    name: "Koneswaram Temple",
    category: "heritage",
    signature: true,
    image: "./attraction-koneswaram-1.jpg",
    tag: "Trincomalee",
    blurb:
      "A Hindu shrine to Shiva perched nearly 100 metres above the sea on Trincomalee's Swami Rock - one of the island's five sacred Pancha Ishwarams.",
  },
  {
    name: "Colombo National Museum",
    category: "heritage",
    blurb:
      "The island's largest museum, tracing 2,500 years of Sri Lankan history through royal regalia, ancient sculpture and colonial-era artefacts.",
  },
  {
    name: "Independence Square",
    category: "heritage",
    blurb:
      "A ceremonial plaza built around a replica of the old State Council chamber, marking the 1948 transfer of power from British rule.",
  },
  {
    name: "Cultural Dance Show",
    category: "heritage",
    blurb:
      "An evening of Kandyan drumming and masked devil dances - a living tradition once performed to ward off illness and bring good harvests.",
  },
  {
    name: "Gem Museum & Lapidary",
    category: "heritage",
    blurb:
      "Sri Lanka has mined sapphires and rubies for over 2,000 years; a lapidary tour shows the cutting and polishing behind the island's gem trade.",
  },
  {
    name: "Kandy City Tour",
    category: "heritage",
    blurb:
      "The last capital to fall to colonial rule, walkable around its lake and market streets, with the hillside Bahirawakanda Buddha for a panoramic close.",
  },
  {
    name: "Dambulla Cave Temple",
    category: "heritage",
    tag: "UNESCO World Heritage",
    blurb:
      "Five candlelit caves holding 153 Buddha statues and 2,100 square metres of ceiling murals, carved into a granite outcrop since the 1st century BC.",
  },
  {
    name: "Spice Garden",
    category: "heritage",
    blurb:
      "Cinnamon, cardamom, pepper and vanilla growing side by side in the Matale hills - Sri Lanka has been the world's source of true cinnamon for centuries.",
  },
  {
    name: "Pidurangala Rock",
    category: "heritage",
    blurb:
      "Sigiriya's quieter, cheaper neighbour - a steeper scramble that rewards with the best photograph of Sigiriya itself, especially at sunrise.",
  },

  // Wildlife & Safari
  {
    name: "Pinnawala Elephant Orphanage",
    category: "wildlife",
    signature: true,
    image: "./tour-pinnawela-1.jpg",
    tag: "Daily bathing hour",
    blurb:
      "A herd of around 60 elephants, from newborns to elderly matriarchs, gathers for a riverside bathing hour that's become one of the island's signature sights.",
  },
  {
    name: "Yala Safari",
    category: "wildlife",
    signature: true,
    span: "col-span-2",
    image: "./attraction-yala-leopard-1.jpg",
    tag: "Highest leopard density on Earth",
    blurb:
      "Up to one leopard per square kilometre in prime blocks makes Yala Sri Lanka's flagship big-cat safari, alongside elephants, sloth bears and crocodiles.",
  },
  {
    name: "Minneriya National Park",
    category: "wildlife",
    signature: true,
    image: "./attraction-minneriya-1.jpg",
    tag: "\"The Gathering\" · Jul – Oct",
    blurb:
      "Between July and October, hundreds of wild elephants converge on the ancient Minneriya tank for one of Asia's largest elephant congregations.",
  },
  {
    name: "Whale & Dolphin Watching",
    category: "wildlife",
    signature: true,
    image: "./tour-mirissa-whale-1.jpg",
    tag: "Mirissa: Dec – Mar",
    blurb:
      "Mirissa sees blue whales at 80-90% sighting rates between December and March; Trincomalee's deeper waters add sperm whale pods from May to August.",
  },
  {
    name: "Elephant Transit Centre",
    category: "wildlife",
    tag: "Udawalawe",
    blurb:
      "Inside Udawalawe National Park, this rehabilitation centre has bottle-fed and released over 200 orphaned elephant calves back into the wild since 1995.",
  },
  {
    name: "Wilpattu Safari",
    category: "wildlife",
    blurb:
      "Named for its \"villus\" - natural rain-fed lakes - Wilpattu is quieter than Yala, with elephant herds up to 100 strong gathering at dawn.",
  },
  {
    name: "Udawalawe Safari",
    category: "wildlife",
    blurb:
      "Open grassland around a reservoir makes for some of the most reliable elephant sightings on the island, without dense jungle cover to hide behind.",
  },
  {
    name: "Elephant Ride",
    category: "wildlife",
    blurb:
      "A gentler, slower way to see the countryside, guided by a mahout through forest trails around the Cultural Triangle.",
  },

  // Hill Country & Nature
  {
    name: "Ella",
    category: "hills",
    signature: true,
    span: "col-span-2",
    image: "./tour-ella-1.png",
    tag: "1,041m elevation",
    blurb:
      "A hiker's hill town reached by one of the world's great train journeys, with Little Adam's Peak and Ella Rock both a short walk from the platform.",
  },
  {
    name: "Nine Arch Bridge",
    category: "hills",
    signature: true,
    image: "./home-grid-5.png",
    tag: "Demodara, near Ella",
    blurb:
      "A colonial-era railway viaduct, 91 metres long and 24 metres high, timed by most visitors to a passing train.",
  },
  {
    name: "Horton Plains & World's End",
    category: "hills",
    signature: true,
    image: "./attraction-worlds-end-1.jpg",
    tag: "Arrive by 7am",
    blurb:
      "A misty montane plateau ending at an 880-metre escarpment - go early, before the valley cloud rolls in and swallows the view by mid-morning.",
  },
  {
    name: "Mini Adam's Peak",
    category: "hills",
    blurb:
      "A shorter, gentler alternative to the pilgrim trail up the real Adam's Peak, with a 360-degree view over Ella's tea-terraced valley.",
  },
  {
    name: "Nuwara Eliya City Tour",
    category: "hills",
    blurb:
      "Sri Lanka's \"Little England\" - Tudor cottages, a golf course and a lake, built as a hill-station retreat for British colonial administrators.",
  },
  {
    name: "Victoria Park",
    category: "hills",
    blurb:
      "A manicured Nuwara Eliya garden known for its rose beds, migrant birdlife and the cool mountain air that made this town a colonial summer capital.",
  },
  {
    name: "Hakgala Botanical Garden",
    category: "hills",
    blurb:
      "A high-altitude garden founded in 1861 for cinchona and later roses, set against cloud-forest slopes near Nuwara Eliya.",
  },
  {
    name: "Strawberry Fields",
    category: "hills",
    blurb:
      "Nuwara Eliya's cool climate supports the island's only strawberry farms, open for picking and fresh cream teas.",
  },
  {
    name: "Tea Factory Visit",
    category: "hills",
    blurb:
      "See leaf become Ceylon tea in one afternoon - withering, rolling, oxidising and firing - in a working factory in the hill country.",
  },
  {
    name: "Peradeniya Botanical Garden",
    category: "hills",
    blurb:
      "147 acres near Kandy holding over 300 orchid varieties, giant bamboo and an avenue of century-old palms - one of Asia's finest botanical gardens.",
  },
  {
    name: "Hot Air Balloon Ride",
    category: "hills",
    tag: "Feb – Apr, Aug – Sep",
    blurb:
      "A dawn float over the Cultural Triangle's ancient tanks and forest canopy, Sigiriya rising out of the morning mist below.",
  },

  // Beaches & Adventure
  {
    name: "Hikkaduwa Coral Gardens & Beach",
    category: "coast",
    signature: true,
    image: "./tour-hikkaduwa-1.jpg",
    tag: "Shallow reef snorkeling",
    blurb:
      "A shallow coral sanctuary just offshore, with reef fish and sea turtles visible on a snorkel straight from the sand.",
  },
  {
    name: "Unawatuna Beach",
    category: "coast",
    blurb:
      "A sheltered, palm-fringed bay near Galle Fort, calm enough for swimming most of the year and a launch point for reef snorkeling.",
  },
  {
    name: "Mirissa",
    category: "coast",
    blurb:
      "A crescent beach and fishing harbour on the south coast, doubling as Sri Lanka's best-known departure point for blue whale excursions.",
  },
  {
    name: "Bentota",
    category: "coast",
    blurb:
      "A river-meets-ocean resort town, with the Bentota Ganga good for watersports and a beach among the calmest on the south-west coast.",
  },
  {
    name: "Bentota Water Sports",
    category: "coast",
    blurb:
      "Jet-skiing, banana boats and windsurfing on the sheltered Bentota River, alongside deeper-water options out on the open coast.",
  },
  {
    name: "Turtle Hatchery",
    category: "coast",
    blurb:
      "Conservation projects along the south coast protect nesting sea turtles and release hatchlings - five of the world's seven marine turtle species nest in Sri Lanka.",
  },
  {
    name: "Madu River Boat Ride",
    category: "coast",
    blurb:
      "A mangrove-lined river safari near Balapitiya, drifting past a cinnamon island and small Buddhist temple islets.",
  },
  {
    name: "Snorkeling & Diving",
    category: "coast",
    blurb:
      "Warm, clear water and shallow reefs make the south and east coasts approachable for first-time snorkelers and certified divers alike.",
  },
  {
    name: "Surfing",
    category: "coast",
    blurb:
      "Point breaks and beach breaks from Arugam Bay to Weligama, with a season on almost every coast depending on the monsoon.",
  },
  {
    name: "Kite Surfing in Kalpitiya",
    category: "coast",
    tag: "May – Oct & Dec – Mar",
    blurb:
      "Steady trade winds nine months of the year, split between a stronger southwest season and a gentler, learner-friendly winter season.",
  },
  {
    name: "Kalpitiya Dutch Fort",
    category: "coast",
    blurb:
      "A small 17th-century coastal fort on the Kalpitiya peninsula, one of the quieter Dutch-era ruins on the island.",
  },
  {
    name: "Water Sports in Passikudah",
    category: "coast",
    blurb:
      "A shallow, coral-protected bay on the east coast, calm enough to wade far offshore - ideal for beginners on a paddleboard or kayak.",
  },
  {
    name: "Whitewater Rafting",
    category: "coast",
    tag: "Kitulgala · Grade II–III",
    blurb:
      'Rapids on the Kelani River, a two-to-three-hour run through a forested gorge close to where "The Bridge on the River Kwai" was filmed.',
  },

  // City Life
  {
    name: "Negombo",
    category: "city",
    signature: true,
    image: "./attraction-negombo-1.jpg",
    tag: "15 min from the airport",
    blurb:
      "A historic fishing town whose lagoon and Dutch canal are still worked by the same outrigger boats and cast nets used for generations.",
  },
  {
    name: "Colombo City Tour",
    category: "city",
    blurb:
      "Fort's colonial streets, Pettah's spice bazaar and the Gangaramaya temple complex, within the same low humid skyline that has watched the harbour for a century.",
  },
  {
    name: "Colombo Shopping",
    category: "city",
    blurb:
      "From Dutch Hospital boutiques to Pettah's bargain bazaars, the capital covers both ends of Sri Lanka's shopping spectrum in one afternoon.",
  },
  {
    name: "Negombo City Tour",
    category: "city",
    blurb:
      "The fish market, the Dutch canal and a lagoon boat ride - an easy first or last stop for flights in and out of Bandaranaike International Airport.",
  },
];

@Component({
  selector: "app-attractions",
  imports: [RouterLink, Icon, Reveal, Tilt],
  template: `
    <section class="grain relative pt-40 pb-24 surface-ink overflow-hidden">
      <div
        class="mesh-blob mesh-blob-a w-[460px] h-[460px] -top-32 -right-24 bg-saffron/15"
      ></div>
      <div
        class="mesh-blob mesh-blob-b w-[320px] h-[320px] bottom-0 left-0 bg-teal/10"
      ></div>
      <div class="container relative z-10">
        <div class="flex items-center gap-2 mb-6" appReveal="fade">
          <div class="w-8 h-[2px] bg-saffron"></div>
          <span
            class="text-saffron font-bold uppercase tracking-[0.3em] text-[10px]"
            >Discover The Island</span
          >
        </div>
        <h1
          class="text-6xl md:text-8xl font-serif leading-[0.9] tracking-tight max-w-4xl"
          appReveal
          [revealDelay]="0.1"
        >
          Top <span class="italic font-light text-paper/50">Attractions</span>
        </h1>
        <p
          class="max-w-xl text-paper/60 leading-relaxed text-lg mt-6"
          appReveal
          [revealDelay]="0.2"
        >
          From a 5th-century sky palace to a coral reef you can snorkel from
          the beach - {{ attractions.length }} places worth building a route
          around, sorted the way you'd actually plan a trip.
        </p>

        <div
          class="flex flex-wrap gap-x-10 gap-y-4 mt-12 pt-8 border-t border-paper/10"
          appReveal
          [revealDelay]="0.3"
        >
          @for (c of categories; track c.key) {
            <div class="flex items-center gap-2.5">
              <div
                class="w-8 h-8 rounded-full flex items-center justify-center bg-paper/10 text-saffron"
              >
                <app-icon [name]="c.icon" [size]="14" />
              </div>
              <span class="text-paper/60 text-xs font-medium">{{
                c.label
              }}</span>
            </div>
          }
        </div>
      </div>
    </section>

    <!-- Signature attractions -->
    <section class="py-24">
      <div class="container">
        <div
          class="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-14"
          appReveal="fade"
        >
          <h2 class="text-4xl md:text-5xl font-serif leading-tight max-w-lg">
            The island's headline acts
          </h2>
          <p class="text-ink/50 text-sm max-w-xs">
            Fourteen places that earn a special trip on their own, not just a
            stop along the way.
          </p>
        </div>

        <div
          class="grid grid-cols-2 lg:grid-cols-4 auto-rows-[170px] sm:auto-rows-[190px] lg:auto-rows-[210px] grid-flow-dense gap-4"
        >
          @for (a of signatureAttractions; track a.name; let i = $index) {
            <div
              appTilt
              [appTiltMax]="3"
              [class]="
                'relative overflow-hidden rounded-[1.5rem] group ' +
                (a.span ?? '')
              "
              appReveal="fade"
              [revealDelay]="Math.min(i, 8) * 0.05"
            >
              <img
                [src]="a.image"
                [alt]="a.name"
                class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                referrerpolicy="no-referrer"
              />
              <div
                class="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/10 to-transparent"
              ></div>
              @if (a.tag) {
                <div
                  class="absolute top-3 left-3 route-code text-[10px] glass-ink text-paper px-2.5 py-1 rounded-full"
                >
                  {{ a.tag }}
                </div>
              }
              <div class="absolute bottom-0 left-0 right-0 p-4 sm:p-5">
                <h3 class="font-serif text-lg sm:text-xl text-paper">
                  {{ a.name }}
                </h3>
                <p
                  class="text-paper/60 group-hover:text-paper/85 text-xs sm:text-sm leading-relaxed line-clamp-1 group-hover:line-clamp-3 transition-all duration-300 mt-1"
                >
                  {{ a.blurb }}
                </p>
              </div>
            </div>
          }
        </div>
      </div>
    </section>

    <!-- Full directory -->
    <section class="py-24 bg-paper-soft">
      <div class="container">
        <div
          class="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-4"
          appReveal="fade"
        >
          <h2 class="text-4xl md:text-5xl font-serif leading-tight max-w-xl">
            The full index
          </h2>
          <p class="text-ink/50 text-sm max-w-xs">
            Filtered the way you'd actually plan a trip - by what kind of day
            you're after.
          </p>
        </div>

        <div
          class="flex flex-wrap gap-x-7 gap-y-3 mb-14 pb-6 border-b border-sand/70"
          appReveal="fade"
          [revealDelay]="0.1"
        >
          <button
            type="button"
            (click)="activeCategory.set('all')"
            [class]="tabClass('all')"
          >
            All
            <span class="text-ink/35 font-normal">{{
              attractions.length
            }}</span>
          </button>
          @for (c of categories; track c.key) {
            <button
              type="button"
              (click)="activeCategory.set(c.key)"
              [class]="tabClass(c.key)"
            >
              {{ c.label }}
              <span class="text-ink/35 font-normal">{{
                countFor(c.key)
              }}</span>
            </button>
          }
        </div>

        @if (activeCategory() === "all") {
          @for (c of categories; track c.key) {
            <div class="mb-16 last:mb-0" appReveal="fade">
              <div class="flex items-baseline gap-3 mb-6">
                <app-icon [name]="c.icon" [size]="16" class="text-teal" />
                <h3 class="font-serif text-2xl italic text-ink/80">
                  {{ c.label }}
                </h3>
                <div class="h-px flex-1 bg-sand/60"></div>
              </div>
              <div class="columns-1 md:columns-2 gap-x-12">
                @for (a of byCategory(c.key); track a.name; let i = $index) {
                  <div
                    class="flex gap-4 py-4 border-b border-sand/40 break-inside-avoid"
                  >
                    <div class="route-code text-xs text-sand pt-1 w-6 shrink-0">
                      {{ pad(i + 1) }}
                    </div>
                    <div class="min-w-0">
                      <div class="flex items-baseline gap-2 flex-wrap">
                        <h4 class="font-serif text-lg leading-snug">
                          {{ a.name }}
                        </h4>
                        @if (a.tag) {
                          <span class="text-vermilion text-[11px] font-medium">
                            {{ a.tag }}
                          </span>
                        }
                      </div>
                      <p class="text-ink/55 text-sm leading-relaxed mt-1">
                        {{ a.blurb }}
                      </p>
                    </div>
                  </div>
                }
              </div>
            </div>
          }
        } @else {
          <div class="columns-1 md:columns-2 gap-x-12" appReveal="fade">
            @for (a of filteredAttractions(); track a.name; let i = $index) {
              <div
                class="flex gap-4 py-4 border-b border-sand/40 break-inside-avoid"
              >
                <div class="route-code text-xs text-sand pt-1 w-6 shrink-0">
                  {{ pad(i + 1) }}
                </div>
                <div class="min-w-0">
                  <div class="flex items-baseline gap-2 flex-wrap">
                    <h4 class="font-serif text-lg leading-snug">
                      {{ a.name }}
                    </h4>
                    @if (a.tag) {
                      <span class="text-vermilion text-[11px] font-medium">
                        {{ a.tag }}
                      </span>
                    }
                  </div>
                  <p class="text-ink/55 text-sm leading-relaxed mt-1">
                    {{ a.blurb }}
                  </p>
                </div>
              </div>
            }
          </div>
        }
      </div>
    </section>

    <section class="py-28">
      <div class="container">
        <div
          class="relative overflow-hidden bg-ink rounded-[2.5rem] p-14 sm:p-20 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-10"
          appReveal="scale"
        >
          <div
            class="mesh-blob mesh-blob-a w-[380px] h-[380px] -top-24 -right-20 bg-saffron/15"
          ></div>
          <div class="max-w-xl space-y-5 relative z-10">
            <h2 class="text-4xl sm:text-5xl font-serif leading-tight text-paper">
              Pick the stops,
              <span class="italic font-light text-paper/50"
                >we'll build the route.</span
              >
            </h2>
            <p class="text-paper/60 leading-relaxed">
              Every itinerary on this site is a starting point - tell us
              which of these you don't want to miss and we'll route around
              them.
            </p>
          </div>
          <div class="flex flex-col sm:flex-row gap-4 shrink-0 relative z-10">
            <a routerLink="/tours" class="btn-saffron justify-center"
              >Browse Tours</a
            >
            <a routerLink="/contact" class="btn-outline-light justify-center"
              >Plan My Route</a
            >
          </div>
        </div>
      </div>
    </section>
  `,
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
})
export class Attractions {
  Math = Math;
  categories = CATEGORIES;
  attractions = ATTRACTIONS;
  signatureAttractions = ATTRACTIONS.filter((a) => a.signature);

  activeCategory = signal<CategoryKey | "all">("all");

  filteredAttractions = computed(() => {
    const cat = this.activeCategory();
    if (cat === "all") return this.attractions;
    return this.attractions.filter((a) => a.category === cat);
  });

  countFor(key: CategoryKey) {
    return this.attractions.filter((a) => a.category === key).length;
  }

  byCategory(key: CategoryKey) {
    return this.attractions.filter((a) => a.category === key);
  }

  pad(n: number) {
    return n < 10 ? `0${n}` : `${n}`;
  }

  tabClass(key: CategoryKey | "all") {
    const base =
      "text-sm font-medium pb-1 border-b-2 transition-colors flex items-baseline gap-1.5";
    return this.activeCategory() === key
      ? `${base} border-vermilion text-ink`
      : `${base} border-transparent text-ink/45 hover:text-ink/70`;
  }
}
